/**
 * useAlertImpacts — checks if an entity is affected by active alerts.
 * Used by AlertImpactBadge to show warning icons on KPI cards, domain panels, etc.
 *
 * Deduplicates concurrent requests: when multiple AlertImpactBadge instances
 * mount simultaneously, only ONE /api/alerts/impacts call is made. The pending
 * promise is shared via module-level state.
 */
import { useState, useEffect, useCallback, useContext } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ConsumerUnitContext } from '../contexts/ConsumerUnitContext';
import { fetchAlertImpacts } from '../services/alertService';
import { useEventStream } from './useEventStream';

const USE_MOCK = process.env.REACT_APP_MOCK === 'true';
const CACHE_TTL_MS = 30_000;
const BATCH_DELAY_MS = 16; // ~1 frame — collect entity keys before firing

// Module-level shared state
let impactCache = { data: null, ts: 0 };
let pendingBatch = null; // { keys: Set<string>, promise: Promise, resolve, timer }

/**
 * Collect entity keys from all hook instances mounting in the same tick,
 * then fire a single batched request.
 */
function requestBatch(entityKey, unitId, getToken) {
  if (impactCache.data && Date.now() - impactCache.ts < CACHE_TTL_MS) {
    return Promise.resolve(impactCache.data);
  }

  if (pendingBatch) {
    // Another instance already scheduled a batch — piggyback on it
    pendingBatch.keys.add(entityKey);
    return pendingBatch.promise;
  }

  // First caller in this tick — start a new batch
  let batchResolve;
  const promise = new Promise((resolve) => { batchResolve = resolve; });

  const keys = new Set([entityKey]);
  const timer = setTimeout(async () => {
    const batch = pendingBatch;
    pendingBatch = null;
    try {
      const data = await fetchAlertImpacts(unitId, [...batch.keys], getToken);
      impactCache = { data: data.alerts ?? [], ts: Date.now() };
      batch.resolve(impactCache.data);
    } catch {
      batch.resolve([]);
    }
  }, BATCH_DELAY_MS);

  pendingBatch = { keys, promise, resolve: batchResolve, timer };
  return promise;
}

/** Reset cache — called on SSE invalidation */
export function invalidateImpactCache() {
  impactCache = { data: null, ts: 0 };
}

export function useAlertImpacts(entityType, entityId) {
  const { getToken } = useAuth();
  // Safe access — hook may run before ConsumerUnitProvider mounts
  const consumerCtx = useContext(ConsumerUnitContext);
  const selectedUnitId = consumerCtx?.selectedUnitId || 'all';
  const { subscribe } = useEventStream();
  const [impacts, setImpacts] = useState([]);
  const entityKey = `${entityType}:${entityId}`;

  const load = useCallback(async () => {
    if (USE_MOCK || !entityType || !entityId) return;

    const allAlerts = await requestBatch(entityKey, selectedUnitId, getToken);

    // Filter to alerts that impact this specific entity
    const matching = (allAlerts ?? []).filter((a) =>
      (a.impacted ?? []).some((imp) => imp.type === entityType && imp.id === entityId)
    );
    setImpacts(matching);
  }, [entityType, entityId, entityKey, selectedUnitId, getToken]);

  useEffect(() => { load(); }, [load]);

  // Invalidate cache on new/enriched alerts, then reload
  useEffect(() => {
    const unsub = subscribe('alert.created', () => {
      invalidateImpactCache();
      load();
    });
    const unsub2 = subscribe('alert.enriched', () => {
      invalidateImpactCache();
      load();
    });
    return () => { unsub(); unsub2(); };
  }, [subscribe, load]);

  return { impacts, hasImpact: impacts.length > 0 };
}
